import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// Crew Watch (Pat, Sep 25 2026): field workers on the clock whose QuickBooks Time
// location shows them stopped 30+ minutes off the job, or away from it 45+ minutes.
// The bells already went to the PM and the office; this is the running list, plus the
// places that are fine to be at (the yard, supply houses).
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, fontFamily: 'inherit' };

export default function CrewWatchPanel() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [places, setPlaces] = useState([]);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [addr, setAddr] = useState('');
  const [found, setFound] = useState(null);   // { lat, lng, display_name }
  const [probe, setProbe] = useState(null);
  const isExec = (() => { try { return ['admin', 'Executive'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; } })();

  const load = () => {
    apiFetch('/crew-watch/events').then(d => setEvents(Array.isArray(d) ? d : [])).catch(e => setErr(e?.error || 'Could not load the list.'));
    apiFetch('/crew-watch/places').then(d => setPlaces(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  async function search() {
    if (!addr.trim()) return;
    try { const r = await apiFetch(`/geocode?q=${encodeURIComponent(addr.trim())}`); setFound(r && r[0] ? r[0] : null); if (!r || !r[0]) setErr('Could not find that address.'); }
    catch { setErr('Could not look that up.'); }
  }
  async function addPlace() {
    if (!found || !name.trim()) return;
    try {
      await apiFetch('/crew-watch/places', { method: 'POST', body: JSON.stringify({ name: name.trim(), address: found.display_name, lat: found.lat, lng: found.lng, radius_m: 400 }) });
      setName(''); setAddr(''); setFound(null); load();
    } catch (e) { setErr(e?.error || 'Could not add it.'); }
  }
  async function removePlace(p) {
    if (!window.confirm(`Take "${p.name}" off the allowed places?`)) return;
    try { await apiFetch(`/crew-watch/places/${p.id}`, { method: 'DELETE' }); load(); } catch (e) { setErr(e?.error || 'Could not remove it.'); }
  }
  const when = iso => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 18, alignItems: 'start' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Flags · newest first</div>
        {err && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 700, marginBottom: 8 }}>{err}</div>}
        {events === null ? <div style={{ color: '#6b7280', padding: 20 }}>Loading…</div>
          : events.length === 0 ? <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center', background: '#fff', border: '1px dashed #e5e7eb', borderRadius: 12 }}>Nothing flagged yet. Field workers on the clock are checked every few minutes.</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {events.map(e => (
                <div key={e.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: `5px solid ${e.kind === 'stopped' ? '#dc2626' : '#b45309'}`, borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{e.user_name}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: e.kind === 'stopped' ? '#b91c1c' : '#92400e' }}>{e.kind === 'stopped' ? `stopped ${e.minutes} min off the job` : `away from the job ${e.minutes} min`}</span>
                    <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>{when(e.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 3 }}>
                    {e.job_label && <button onClick={() => e.project_id && navigate(`/admin/new-projects/${e.project_id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', fontWeight: 700, cursor: e.project_id ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: 13 }}>{e.job_label}</button>}
                    {e.address ? ` · at ${e.address}` : ''}
                    {e.lat != null && <a href={`https://www.google.com/maps?q=${e.lat},${e.lng}`} target="_blank" rel="noreferrer" style={{ marginLeft: 8, fontSize: 12, color: '#2563eb' }}>map ›</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Allowed places</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Being stopped here never flags: the yard, supply houses, the fuel stop.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {places.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ flex: 1, minWidth: 0 }}><b>{p.name}</b>{p.address ? <span style={{ color: '#6b7280' }}> · {p.address}</span> : ''}</span>
                <button onClick={() => removePlace(p)} title="Remove" style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 16, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Name — e.g. Codale Reno" style={inp} />
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={addr} onChange={e => { setAddr(e.target.value); setFound(null); }} onKeyDown={e => { if (e.key === 'Enter') search(); }} placeholder="Address" style={{ ...inp, flex: 1 }} />
              <button onClick={search} style={btn}>Find</button>
            </div>
            {found && <div style={{ fontSize: 12, color: '#15803d' }}>✓ {found.display_name}</div>}
            <button onClick={addPlace} disabled={!found || !name.trim()} style={{ ...btn, background: found && name.trim() ? '#1e3a5f' : '#e5e7eb', color: found && name.trim() ? '#fff' : '#9ca3af', border: 'none' }}>Add place</button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
          Rules: stopped 30+ minutes somewhere that is not the job or an allowed place, or away from the job 45+ minutes. Field workers only, while on the clock, from the QuickBooks Time app's location. Lunch (11:30–1:00) is left alone. A job with no pin on the map cannot be checked.
        </div>
        {isExec && (
          <div>
            <button onClick={() => { setProbe('loading'); apiFetch('/crew-watch/probe').then(setProbe).catch(e => setProbe({ error: e?.error || 'failed' })); }} style={btn}>
              {probe === 'loading' ? 'Asking QuickBooks Time…' : 'Check the location feed (Executive)'}
            </button>
            {probe && probe !== 'loading' && (
              <pre style={{ fontSize: 11, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginTop: 8, whiteSpace: 'pre-wrap' }}>{JSON.stringify(probe, null, 2)}</pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
